// src/lib/helpers.js
import { state } from './store.js';

export function daysFromNow(n) { return new Date(Date.now() + n*86400000).toISOString().slice(0,10); }
export function todayStr() { return new Date().toISOString().slice(0,10); }

export function escapeHtml(s) {
  if (s == null) return '';
  return String(s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
}

// Format a number as money in a specific currency.
// Pass a currency code (ZAR/USD/EUR) explicitly when known; falls back to workspace default.
export function fmtMoney(n, currency) {
  if (n == null || n === '') return '—';
  const num = Number(n) || 0;
  const code = currency || state.settings.currency || 'ZAR';
  return code + ' ' + num.toLocaleString(undefined, { maximumFractionDigits: 0 });
}
// Convenience: format a deal's value in the deal's own currency (or workspace default)
export function fmtDealMoney(deal) {
  return fmtMoney(deal.value, deal.currency || state.settings.currency);
}

export function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}
export function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' }) + ' · ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}
export function relativeDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Math.floor((d - new Date(todayStr())) / 86400000);
  if (diff === 0) return 'today';
  if (diff === 1) return 'tomorrow';
  if (diff === -1) return 'yesterday';
  if (diff > 0) return 'in ' + diff + 'd';
  return Math.abs(diff) + 'd ago';
}
export function fmtFileSize(bytes) {
  if (bytes == null) return '—';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
  return (bytes / 1024 / 1024).toFixed(1) + ' MB';
}
export function initials(name) {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/);
  return (parts[0][0] + (parts[1]?parts[1][0]:'')).toUpperCase();
}
// Convert an amount from one currency to another using the workspace FX rates.
// Rates are stored as ZAR-per-foreign, so:
//   foreign -> ZAR: amount * (zar_per_foreign)
//   ZAR -> foreign: amount / (zar_per_foreign)
//   foreign -> foreign: convert through ZAR as the pivot
// Returns null if the relevant rate is missing (caller decides how to display).
export function convertCurrency(amount, fromCur, toCur) {
  if (amount == null || amount === '') return 0;
  const a = Number(amount) || 0;
  if (fromCur === toCur) return a;
  const r = state.fxRates;
  // Source amount in ZAR
  let zar;
  if (fromCur === 'ZAR') zar = a;
  else if (fromCur === 'USD') { if (!r.zarPerUsd) return null; zar = a * r.zarPerUsd; }
  else if (fromCur === 'EUR') { if (!r.zarPerEur) return null; zar = a * r.zarPerEur; }
  else return null;
  // ZAR to target
  if (toCur === 'ZAR') return zar;
  if (toCur === 'USD') { if (!r.zarPerUsd) return null; return zar / r.zarPerUsd; }
  if (toCur === 'EUR') { if (!r.zarPerEur) return null; return zar / r.zarPerEur; }
  return null;
}
// True if all FX rates needed for conversion are set (>0)
export function fxRatesAvailable() {
  return state.fxRates.zarPerUsd > 0 && state.fxRates.zarPerEur > 0;
}
// Convert a deal's value into a target currency (using deal's own currency as source)
export function convertDealValue(deal, toCur) {
  return convertCurrency(deal.value, deal.currency || state.settings.currency, toCur);
}
// Sum a list of deals into a single target currency. Returns { total, missing }
// where missing is the count of deals we couldn't convert (rate missing).
export function sumInCurrency(deals, toCur, valueFn) {
  let total = 0;
  let missing = 0;
  for (const d of deals) {
    const native = valueFn ? valueFn(d) : (Number(d.value) || 0);
    if (!native) continue;
    const dealCur = d.currency || state.settings.currency;
    const conv = convertCurrency(native, dealCur, toCur);
    if (conv === null) missing++;
    else total += conv;
  }
  return { total, missing };
}
export function isAdmin() {
  return !!(state.currentProfile && state.currentProfile.isAdmin && state.currentProfile.isActive);
}
export function getCompany(id) { return state.companies.find(c => c.id === id); }
export function getContact(id) { return state.contacts.find(c => c.id === id); }
export function getDeal(id) { return state.deals.find(d => d.id === id); }
export function getTicket(id) { return state.tickets.find(t => t.id === id); }
export function getAttachment(id) { return state.attachments.find(a => a.id === id); }
export function contactName(c) { if (!c) return '—'; return ((c.firstName||'') + ' ' + (c.lastName||'')).trim() || '—'; }
export function dealStageName(id) { const s = state.settings.dealStages.find(x => x.id === id); return s ? s.name : id; }
export function dealStatus(id) { return state.settings.dealStatuses.find(x => x.id === id) || state.settings.dealStatuses[0]; }
export function ticketStageName(id) { const s = state.settings.ticketStages.find(x => x.id === id); return s ? s.name : id; }

// ---------- toast ----------
export function toast(msg, isError) {
  const root = document.getElementById('toast-root');
  root.innerHTML = '<div class="toast' + (isError ? ' error' : '') + '">' + escapeHtml(msg) + '</div>';
  clearTimeout(window._toastT);
  window._toastT = setTimeout(() => { root.innerHTML = ''; }, 2600);
}

// ---------- modal ----------
export function showModal({ title, body, footer, wide, onClose }) {
  const root = document.getElementById('modal-root');
  root.innerHTML = `
    <div class="modal-backdrop" id="modal-backdrop">
      <div class="modal ${wide?'wide':''}" role="dialog" aria-modal="true">
        <div class="modal-head">
          <h2>${escapeHtml(title)}</h2>
          <button class="modal-close" id="modal-close" aria-label="Close">×</button>
        </div>
        <div class="modal-body" id="modal-body"></div>
        ${footer ? '<div class="modal-foot" id="modal-foot"></div>' : ''}
      </div>
    </div>`;
  document.getElementById('modal-body').innerHTML = body;
  if (footer) document.getElementById('modal-foot').innerHTML = footer;
  const close = () => { root.innerHTML = ''; if (onClose) onClose(); };
  document.getElementById('modal-close').onclick = close;
  document.getElementById('modal-backdrop').addEventListener('click', (e) => {
    if (e.target.id === 'modal-backdrop') close();
  });
  document.addEventListener('keydown', function esc(e){
    if (e.key === 'Escape') { close(); document.removeEventListener('keydown', esc); }
  });
  return close;
}
export function closeModal() { document.getElementById('modal-root').innerHTML = ''; }

export async function runAsync(fn, successMsg) {
  try {
    const result = await fn();
    if (successMsg) toast(successMsg);
    return result;
  } catch (e) {
    console.error(e);
    toast(e.message || 'Something went wrong', true);
    throw e;
  }
}
